import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    ModalsProvider,
    StepControl,
    TooltipShowMore,
} from '@speeki/global-ui-components';
import { IconButton, IconManager } from '@speeki/icons';

import { CountryAnonymityEnabledDocument } from '@graphql/generated/graphql';

import { useReportDetails } from '@utils/hooks/useReportDetails';
import { useResponsive } from '@utils/hooks/useResponsive';

import { getPath } from '@services/RouteService/paths';

import { useQuery, useReactiveVar } from '@apollo/client';
import {
    authVar,
    resetReporterDetails,
    subsidiarySiteVar,
} from '@boot/reactiveVariables';
import { AvatarAssistant } from '@domains/new-conversation/atoms/AvatarAssistant/AvatarAssistant';
import {
    AdditionalDetailsTitle,
    AdditionalDetailsValue,
    AdditionalDetailsWrapper,
    AvatarWrapper,
    DescriptionText,
    DescriptionWrapper,
    IconCloseWrapper,
    ImageCaption,
    ReportDetailsButton,
    ReportDetailsText,
    SidebarContent,
    SidebarFooter,
    SidebarWrapper,
    StyledBackgroundCircle,
} from '@domains/new-conversation/molecules/ConversationSidebar/ConversationSidebar.styled';

export interface SidebarProps {
    children?: React.ReactNode;
    currentStep: number;
    onClose?: () => void;
    preventionModalActive?: boolean;
}

export const ConversationSidebar = ({
    children,
    currentStep,
    onClose,
    preventionModalActive,
}: SidebarProps) => {
    const isGuest = useReactiveVar(authVar).isGuest;
    const { t } = useTranslation();
    const isMd = useResponsive('md');
    const navigate = useNavigate();
    const { data: countryAnonymityData } = useQuery(
        CountryAnonymityEnabledDocument,
        { context: { notifyOnError: true }, fetchPolicy: 'network-only' },
    );

    const { additionalDetails, conversationDetailsData, excludedManagers } =
        useReportDetails();

    const closeReport = () => {
        resetReporterDetails();
        subsidiarySiteVar(undefined);
        navigate(getPath({ path: '/' }));
    };

    return (
        <SidebarWrapper>
            <IconCloseWrapper data-testid="9Wr0MlR">
                <IconButton
                    iconProps={{
                        icon: 'SvgCloseBig24',
                        size: 24,
                        themeColors: {
                            default: {
                                'color-1': 'input.primaryBg',
                            },
                        },
                    }}
                    onClick={() => {
                        onClose?.();
                        preventionModalActive
                            ? ModalsProvider.open('LeaveConfirmationModal')
                            : closeReport();
                    }}
                    testId="wjKvocg"
                />
            </IconCloseWrapper>

            <SidebarContent>
                <div>
                    <AvatarWrapper>
                        <AvatarAssistant isMd={isMd} />
                        <ImageCaption>{t('SPEAK_UP')}</ImageCaption>
                    </AvatarWrapper>

                    {!isMd &&
                        (conversationDetailsData ? (
                            <DescriptionWrapper>
                                <DescriptionText>
                                    {t('ADDITIONAL_DETAILS_HEADER')}
                                </DescriptionText>
                            </DescriptionWrapper>
                        ) : (
                            <DescriptionWrapper>
                                <DescriptionText>
                                    {t('NEW_CONVERSATION_TEXT_1')}
                                </DescriptionText>
                                {countryAnonymityData?.isEnabled && (
                                    <DescriptionText>
                                        {t('NEW_CONVERSATION_TEXT_2')}
                                    </DescriptionText>
                                )}
                            </DescriptionWrapper>
                        ))}
                </div>

                {!isMd && !!additionalDetails.length && (
                    <AdditionalDetailsWrapper>
                        {additionalDetails.map((detail) => (
                            <div key={detail.key}>
                                <AdditionalDetailsTitle>
                                    {detail.title}
                                </AdditionalDetailsTitle>
                                <AdditionalDetailsValue>
                                    {detail.value}
                                </AdditionalDetailsValue>
                            </div>
                        ))}
                        {excludedManagers && (
                            <div>
                                <AdditionalDetailsTitle>
                                    {t('ADDITIONAL_DETAILS_MANAGERS')}
                                </AdditionalDetailsTitle>
                                <AdditionalDetailsValue>
                                    <TooltipShowMore
                                        color={'primary'}
                                        data={excludedManagers}
                                        testId="9qBEL1u"
                                    />
                                </AdditionalDetailsValue>
                            </div>
                        )}
                    </AdditionalDetailsWrapper>
                )}
            </SidebarContent>

            <SidebarFooter>
                {isMd ? (
                    <ReportDetailsButton
                        onClick={() =>
                            ModalsProvider.open('ReportDetailsModal')
                        }
                    >
                        <ReportDetailsText>
                            {t('SEE_REPORT_DETAILS')}
                        </ReportDetailsText>
                        <IconManager
                            icon="SvgChevronRight24"
                            size={24}
                            themeColors={{
                                default: {
                                    'color-1': 'input.primaryBg',
                                },
                            }}
                        />
                    </ReportDetailsButton>
                ) : (
                    <>
                        <StepControl
                            activeStep={currentStep}
                            length={isGuest ? 4 : 3}
                            testId="WPVgdgv"
                            variant="light"
                        />
                        {children}
                    </>
                )}
            </SidebarFooter>
            {!isMd && <StyledBackgroundCircle />}
        </SidebarWrapper>
    );
};
